<template>
    <main class="p-2 w-full h-full flex flex-col items-start">
        <div class="flex w-full flex-grow flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th class="text-left border-r p-1" style="width: 15rem">{{ $t('commons.bundle') }}</th>
                        <th class="text-left border-r p-1">{{ $t('commons.object') }}</th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <span>{{ $t('commons.amountHT') }}</span>
                        </th>
                        <th style="width: 6rem" class="text-center border-r p-1">
                            {{ $t('commons.status') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="cie in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="text-left border-r p-1">
                                {{ cie.bundle ? cie.bundle.label : '' }}
                            </td>
                            <td class="border-r p-1">
                                <template v-if="cie.cieObject">
                                    <a
                                        :href="`https://tex.moe/project/${$route.params.projectId}/cie/${cie.id}`"
                                        class="flex gap-1"
                                    >
                                        <span class="font-bold">{{ cie.object }}</span>
                                        <span class="">- {{ cie.bundleTo.label }} -</span>
                                        <span>{{ cie.cieObject }}</span>
                                        <span v-if="cie.code" class="mx-1">#{{ cie.code }}</span>
                                        <span v-if="!cie.object || cie.object.trim().length === 0">...</span>
                                    </a>
                                </template>
                                <template v-else>
                                    <a
                                        :href="`https://tex.moe/project/${$route.params.projectId}/cie/${cie.id}`"
                                        class="flex gap-1"
                                    >
                                        <span class="font-bold">{{ cie.object }}</span>
                                        <span v-if="cie.code" class="mx-1">#{{ cie.code }}</span>
                                        <span v-if="!cie.object || cie.object.trim().length === 0">...</span>
                                    </a>
                                </template>
                            </td>
                            <td
                                class="border-r p-1 text-right"
                                :class="{
                                    'text-green-500': cie.amountTTC >= 0,
                                    'text-red-600': cie.amountTTC < 0,
                                }"
                            >
                                {{
                                    cie.amountTTC
                                        ? (cie.amountTTC > 0 ? '+' : '') + $n(cie.amountTTC, currencyFormat)
                                        : '-'
                                }}
                            </td>
                            <td class="text-left border-r p-1" :class="cie.quoteStatusColor">
                                {{ $t('amendments.quoteStatus.' + cie.quoteStatus) }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </main>
</template>

<script>
import { filterMatch, getMapById, sortBy, uniqBy } from '@/services/sanitize.service';
import { queryProject } from '@/features/projects/projects.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { getCIEExport } from '@/features/cie/CIEExports.service';
import { getCIEs } from '@/features/cie/CIE.service';

export default {
    components: {},
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
        });
        this.init();
    },
    computed: {
        filteredItems() {
            let result = this.filterFn(this.filterValue);
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'object') {
                        return item.object;
                    } else if (this.sortKey === 'amountTTC') {
                        return item.amountTTC;
                    } else if (this.sortKey === 'quoteStatus') {
                        return item.quoteStatus;
                    } else if (this.sortKey === 'emitter') {
                        return item.bundle ? item.bundle.label : null;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                ]).subscribe(([bundles, cies, companies]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.items = uniqBy(cies, 'groupId')
                        .filter((cie) => cie.type === 'cie')
                        .map((cie) => {
                            const bundle = this.bundleMap[cie.bundleId];
                            return {
                                ...cie,
                                bundle,
                                filterString: [cie.code, cie.object].join(),
                                quoteStatusColor: this.quoteStatusColors[cie.quoteStatus] || '',
                            };
                        })
                        .reduce(
                            (acc, cie) => [
                                ...acc,
                                cie,
                                ...cie.distribution.map((distribution) => ({
                                    id: cie.id,
                                    bundleId: distribution.bundleId,
                                    bundle: this.bundleMap[distribution.bundleId],
                                    bundleTo: this.bundleMap[cie.bundleId],
                                    amountTTC: -distribution.amount,
                                    quoteStatus: cie.quoteStatus,
                                    filterString: [cie.code, cie.object].join(),
                                    object: this.$t('cie.engagedCIEShort'),
                                    cieObject: cie.object,
                                    quoteStatusColor: this.quoteStatusColors[cie.quoteStatus] || '',
                                    code: cie.code,
                                })),
                            ],
                            [],
                        );
                    this.restoreFilter();
                    this.loading = false;
                }),
            ];
        },
        async restoreFilter() {
            const CIEExport = await getCIEExport(this.$route.params.projectId, this.$route.params.CIEExportId);
            if (CIEExport) {
                this.filterValue = CIEExport.filter || [];
                this.sortKey = CIEExport.sortKey || 'object';
            }
        },
        matchBundleFilter(item, bundleIds) {
            return item.bundle && bundleIds.includes(item.bundle.id);
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchState(stateCriteria, item) {
            if (!stateCriteria || stateCriteria.length === 0) {
                return true;
            }
            return stateCriteria.find(
                (criteria) =>
                    (criteria.id === 'quote_coming' && this.comingStatus.includes(item.quoteStatus)) ||
                    (item.quoteStatus && criteria.id === 'quote_' + item.quoteStatus),
            );
        },
        matchType(criteria, item) {
            if (!criteria || criteria.length === 0) {
                return true;
            }
            return criteria.find(
                (criteria) => (criteria.id === 'cie' && item.type === 'cie') || criteria.id === 'engagedCIE',
            );
        },
        filterFn(filter) {
            const bundleIds = [...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id)];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const stateCriteria = filter.filter((aCriteria) => aCriteria._isStateCriteria);
            const typeCriteria = filter.filter((aCriteria) => aCriteria._isTypeCriteria);

            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchString: this.matchString(stringCriteria, item),
                    matchState: this.matchState(stateCriteria, item),
                    typeCriteria: this.matchType(typeCriteria, item),
                };
                return Object.values(fullCriteria).every((value) => !!value);
            });
        },
    },
    data() {
        return {
            quoteStatusColors: {
                toEmit: 'bg-amber-500',
                toAnalyse: 'text-white bg-gray-500',
                toNegotiate: '',
                toValidateMOA: 'bg-cyan-500',
                validated: 'bg-green-600',
                rejected: 'text-white bg-red-600',
                obsolete: 'text-white bg-red-600',
                market: '',
                marketOption: '',
                noImpact: '',
            },
            comingStatus: ['toEmit', 'toAnalyse', 'toNegotiate', 'toValidateMOA'],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            bundleMap: {},
            loading: 0,
            sortKey: 'name',
            sortAsc: true,
            subscriptions: [],
            bundles: [],
            companies: [],
            items: [],
            filterValue: [],
        };
    },
};
</script>
